import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from "@angular/router";

import { ReauthRequiredError, UserSessionService } from "@shared/services/user-session.service";
import { AuthorizationType } from "@shared/models/authorization.model";
import { APP_403 } from "../config/app-urls";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

function evaluateAuthorization(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    router: Router,
    userSessionService: UserSessionService
): boolean | UrlTree {
    const { requiresAdmin } = activatedRouteSnapshot.data;
    const userSession = userSessionService.userSession;
    const requiredAuthorizationType: AuthorizationType = activatedRouteSnapshot.data.authorizationRequired;

    if (!userSession) {
        return router.createUrlTree(['/']);
    }

    if (requiresAdmin) {
        const isAdmin = userSession.isAdmin === true;
        return isAdmin || router.createUrlTree(['/']);
    }

    if (requiredAuthorizationType) {
        const isAuthorized = userSession.authorizations?.[requiredAuthorizationType] === true;
        return isAuthorized || router.createUrlTree(APP_403.toRouterLinkCommands());
    }

    // No specific authorization required: allow only if the user has at least one authorization enabled
    const hasAnyAuthorization = !!userSession.authorizations
        && Object.values(userSession.authorizations).includes(true);
    return hasAnyAuthorization || router.createUrlTree(APP_403.toRouterLinkCommands());
}

export const authorizationGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot
): boolean | UrlTree | Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const userSessionService = inject(UserSessionService);

    const tryEvaluate = () => evaluateAuthorization(route, router, userSessionService);

    // If session already exists, evaluate synchronously
    if (userSessionService.userSession) {
        return tryEvaluate();
    }

    // Lazily initialize the session for deep links, then evaluate
    return userSessionService.initialize().pipe(
        map(() => tryEvaluate()),
        catchError((error: unknown) => {
            if (error instanceof ReauthRequiredError) {
                return of(router.createUrlTree(['/login'], { queryParams: { error: 'reauth-required' } }));
            }
            return of(router.createUrlTree(['/login'], { queryParams: { error: 'unauthenticated' } }));
        })
    );
};
